package ai.jessie.web.documents

import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardHeadStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.DocumentProgressBarStyle
import ai.jessie.web.theme.DocumentProgressStyle
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldStyle
import ai.jessie.web.theme.NoticeStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.StackStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.card
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.forId
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

// Steps 2-6, A1-A7. A selection is validated at once; only a confirmed one is stored.
@Composable
internal fun documentUpload(
    organisationId: String,
    state: DocumentUiState,
    actions: DocumentActions,
) {
    card(
        title = "Dokument hochladen",
        description =
            "Erlaubt sind $SUPPORTED_TYPES. Eine Datei pro Upload, mindestens 1 Byte und höchstens " +
                "25 MiB, nicht verschlüsselt oder passwortgeschützt.",
    ) {
        Div(attrs = StackStyle.toAttrs()) {
            Div(attrs = FieldStyle.toAttrs()) {
                Label(forId = FILE_INPUT_ID) { Text("Datei auswählen") }
                fileInput(state, actions)
            }
            transferStatus(state)
            uploadProblem(organisationId, state.uploadProblem)
            state.pending?.let { pending ->
                if (state.phase != UploadPhase.VALIDATING) confirmation(pending, state, actions)
            }
            if (state.phase == UploadPhase.VALIDATING)
                Div(attrs = RowStyle.toAttrs()) {
                    themedButton("Abbrechen", actions.cancel, elementId = CANCEL_ID)
                }
            uploadedNotice(organisationId, state)
        }
    }
}

// The file selection; disabled while a transfer runs.
@Composable
private fun fileInput(state: DocumentUiState, actions: DocumentActions) {
    Input(
        InputType.File,
        attrs =
            FieldInputStyle.toAttrs {
                id(FILE_INPUT_ID)
                // Centres the browser's file button in the 38 px field.
                style { property("padding-top", "6px") }
                attr("accept", ACCEPTED_FILES)
                if (state.phase == UploadPhase.UPLOADING) attr("disabled", "")
                onChange { event ->
                    val input = event.target
                    val file = input.files?.item(0)
                    // Cleared at once, so choosing the same file again is a new selection (TC-017).
                    input.value = ""
                    actions.select(file)
                }
            },
    )
}

// Step 9: progress of the current transfer; the server's answer decides the outcome.
@Composable
private fun transferStatus(state: DocumentUiState) {
    val label =
        when (state.phase) {
            UploadPhase.VALIDATING -> "Datei wird geprüft"
            UploadPhase.UPLOADING -> "Dokument wird hochgeladen"
            UploadPhase.IDLE -> return
        }
    val percent = state.progress ?: 0
    P(attrs = StatusStyle.toAttrs { attr("role", "status") }) { Text("$label … $percent %") }
    Div(
        attrs =
            DocumentProgressStyle.toAttrs {
                attr("role", "progressbar")
                attr("aria-label", label)
                attr("aria-valuemin", "0")
                attr("aria-valuemax", "100")
                attr("aria-valuenow", percent.toString())
            }
    ) {
        Div(attrs = DocumentProgressBarStyle.toAttrs { style { property("width", "$percent%") } })
    }
}

// Step 5: the validated name, type and size, then confirm or cancel (A5).
@Composable
private fun confirmation(
    pending: PendingDocument,
    state: DocumentUiState,
    actions: DocumentActions,
) {
    val uploading = state.phase == UploadPhase.UPLOADING
    // An in-card confirmation, as in the invoice progress card (Session 08).
    Section(
        attrs =
            CardStyle.toAttrs {
                id(CONFIRMATION_ID)
                attr("aria-labelledby", "document-confirmation-title")
                attr("tabindex", "-1")
            }
    ) {
        Div(attrs = CardHeadStyle.toAttrs()) {
            H3(attrs = { id("document-confirmation-title") }) { Text("Upload bestätigen") }
        }
        Div(attrs = CardBodyStyle.toAttrs()) {
            facts(
                pending.validation.fileName,
                pending.validation.mediaType,
                pending.validation.sizeBytes,
            )
            Div(attrs = RowStyle.toAttrs()) {
                themedButton(
                    "Hochladen",
                    actions.confirm,
                    variant = ButtonVariant.PRIMARY,
                    disabled = uploading,
                    elementId = CONFIRM_ID,
                )
                themedButton(
                    "Abbrechen",
                    actions.cancel,
                    disabled = uploading,
                    elementId = CANCEL_ID,
                )
            }
        }
    }
    LaunchedEffect(pending) {
        if (!AppGlobals.isExporting)
            (document.getElementById(CONFIRMATION_ID) as? HTMLElement)?.focus()
    }
}

@Composable
private fun uploadProblem(organisationId: String, problem: DocumentProblem?) {
    if (problem == null) return
    Div(
        attrs =
            AlertStyle.toAttrs {
                id(UPLOAD_ERROR_ID)
                attr("role", "alert")
                attr("tabindex", "-1")
            }
    ) {
        Text(problem.message)
        // A3: only the visible existing record the server named.
        problem.existingDocumentId?.let {
            Text(" ")
            A(href = documentHref(organisationId, it)) { Text("Vorhandenes Dokument öffnen") }
        }
    }
    LaunchedEffect(problem) {
        if (!AppGlobals.isExporting)
            (document.getElementById(UPLOAD_ERROR_ID) as? HTMLElement)?.focus()
    }
}

// Step 9 confirmation; after cancelling (A5) focus returns to the file selection.
@Composable
private fun uploadedNotice(organisationId: String, state: DocumentUiState) {
    val notice = state.notice ?: return
    P(
        attrs =
            NoticeStyle.toAttrs {
                id(NOTICE_ID)
                attr("role", "status")
                attr("tabindex", "-1")
            }
    ) {
        Text(notice)
        state.uploaded?.let {
            Text(" ")
            A(href = documentHref(organisationId, it.documentId)) { Text("Dokument öffnen") }
        }
    }
    LaunchedEffect(notice) {
        if (AppGlobals.isExporting) return@LaunchedEffect
        val target = if (state.uploaded != null) NOTICE_ID else FILE_INPUT_ID
        (document.getElementById(target) as? HTMLElement)?.focus()
    }
}

private const val CONFIRMATION_ID = "document-confirmation"
private const val CONFIRM_ID = "document-confirm"
private const val CANCEL_ID = "document-cancel"
private const val UPLOAD_ERROR_ID = "document-upload-error"
private const val NOTICE_ID = "document-notice"
